import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { api } from '../lib/api.js';
import { Cargando, Error as AvisoError } from '../piezas/Estados.jsx';

/**
 * Lo que se le paga a un ingeniero por cada tipo de servicio.
 *
 * `idc_tarifas` existia en el esquema desde el principio con cero filas y
 * cero referencias. A diferencia de las otras tablas asi, su ausencia no
 * dejaba una medida vacia: la dejaba EQUIVOCADA. La medida de costo de la
 * pivote derivaba una tarifa horaria del costo mensual del ingeniero, que
 * solo existe en la modalidad fija, y para la flotante caia en un cero. En la
 * demostracion eran 806 ingenieros flotantes y 6 445 horas de campo con costo
 * cero, y el margen sobreestimado en lo mismo.
 *
 * Un flotante cobra POR SERVICIO. Por eso esta pantalla captura un monto por
 * tipo de servicio y no una tarifa horaria.
 */

interface Renglon {
  tipo_servicio_id: string;
  clave: string;
  nombre: string;
  tarifa_centavos: string | null;
  moneda: string | null;
}

interface Respuesta {
  modalidad: string;
  tarifas: Renglon[];
}

/** Pesos a centavos, sin flotantes de por medio. */
function aCentavos(texto: string): number | null {
  const limpio = texto.replace(/[^\d.]/g, '').trim();
  if (limpio === '') return null;
  const partes = limpio.split('.');
  if (partes.length > 2) return null;
  const enteros = partes[0] ?? '0';
  /* Dos decimales, rellenando: "1.5" son 150 centavos, no 15. */
  const decimales = (partes[1] ?? '').padEnd(2, '0').slice(0, 2);
  const n = Number(`${enteros}${decimales}`);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

const aPesos = (centavos: string | null): string =>
  centavos === null ? '' : (Number(centavos) / 100).toFixed(2);

export function TarifasIdc({
  clave,
  puedeEscribir,
}: {
  /** La clave del ingeniero. */
  clave: string;
  puedeEscribir: boolean;
}): JSX.Element {
  const [filas, setFilas] = useState<Renglon[] | null>(null);
  const [modalidad, setModalidad] = useState<string>('');
  const [error, setError] = useState<unknown>(null);
  const [edicion, setEdicion] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState<string | null>(null);

  /**
   * Recarga desde el servidor.
   *
   * `soloEsta` dice que renglon se acaba de guardar, para no reescribir lo que
   * alguien tenga a medio capturar en otro. Es la misma correccion que las
   * tarifas de proyecto necesitaron: sin eso, guardar un renglon borraba sin
   * aviso lo escrito en los demas.
   */
  const cargar = useCallback(
    (soloEsta?: string) => {
      api
        .obtener<Respuesta>(`/v1/idc/${encodeURIComponent(clave)}/tarifas`)
        .then(({ datos }) => {
          setModalidad(datos.modalidad);
          setFilas(datos.tarifas);
          setEdicion((previo) =>
            Object.fromEntries(
              datos.tarifas.map((r) => [
                r.clave,
                soloEsta === undefined || r.clave === soloEsta
                  ? aPesos(r.tarifa_centavos)
                  : (previo[r.clave] ?? aPesos(r.tarifa_centavos)),
              ]),
            ),
          );
        })
        .catch(setError);
    },
    [clave],
  );

  useEffect(() => cargar(), [cargar]);

  const flotante = modalidad === 'IDC_FLOTANTE';
  const capturadas = (filas ?? []).filter((r) => r.tarifa_centavos !== null).length;

  async function guardar(r: Renglon): Promise<void> {
    const centavos = aCentavos(edicion[r.clave] ?? '');
    setGuardando(r.clave);
    try {
      if (centavos === null) {
        /* Vaciar el campo QUITA la tarifa. Guardar cero seria peor: un
           servicio que de verdad se paga a cero y uno sin tarifa capturada se
           ven igual en un reporte y significan cosas distintas. Aqui ademas
           cambia el costo de la operacion. */
        if (r.tarifa_centavos !== null) {
          await api.quitar(
            `/v1/idc/${encodeURIComponent(clave)}/tarifas/${encodeURIComponent(r.clave)}`,
          );
        }
      } else {
        await api.poner(
          `/v1/idc/${encodeURIComponent(clave)}/tarifas/${encodeURIComponent(r.clave)}`,
          { tarifa_centavos: centavos },
        );
      }
      cargar(r.clave);
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(null);
    }
  }

  return (
    <div className="card" style={{ marginTop: '14px' }} data-prueba="panel-tarifas-idc">
      <div className="toolbar">
        <h3 style={{ margin: 0 }}>Pago por servicio</h3>
        <span className="sp" />
        <span className="chip-dato" data-prueba="tarifas-idc-capturadas">
          {capturadas} de {(filas ?? []).length} capturadas
        </span>
      </div>

      {/* La modalidad cambia el SIGNIFICADO de esta tabla, asi que se dice.
          Dibujarla igual en los dos casos invita a capturar en un fijo algo
          que no va a entrar en ningun calculo. */}
      {filas !== null && !flotante ? (
        <p className="mini" style={{ marginTop: 0 }} data-prueba="aviso-fijo">
          Este ingeniero es de modalidad fija: su costo por hora se deriva de su costo mensual y sus
          horas al mes, asi que estas tarifas no entran en el calculo de costo ni de margen.
        </p>
      ) : (
        <p className="mini" style={{ marginTop: 0 }}>
          Un ingeniero flotante cobra por servicio prestado. De aqui sale su costo en la consulta
          pivote; un servicio sin tarifa capturada no cuenta como cero, cuenta como faltante.
        </p>
      )}

      {error !== null ? (
        <AvisoError error={error} />
      ) : filas === null ? (
        <Cargando />
      ) : (
        <div className="tabla-mapa">
          <table data-prueba="tabla-tarifas-idc">
            <thead>
              <tr>
                <th scope="col">Tipo de servicio</th>
                <th scope="col" className="num">
                  Pago por servicio (MXN)
                </th>
                {puedeEscribir && <th scope="col" />}
              </tr>
            </thead>
            <tbody>
              {filas.map((r) => (
                <tr key={r.clave} data-prueba="fila-tarifa-idc">
                  <th scope="row">
                    {r.nombre}
                    <div className="mini">{r.clave}</div>
                  </th>
                  <td className="num">
                    {puedeEscribir ? (
                      <input
                        value={edicion[r.clave] ?? ''}
                        onChange={(e) => setEdicion({ ...edicion, [r.clave]: e.target.value })}
                        inputMode="decimal"
                        placeholder="sin tarifa"
                        aria-label={`Pago por ${r.nombre}`}
                        style={{ width: 110, textAlign: 'right' }}
                        /* Este campo vive dentro del formulario del
                           ingeniero: un Enter aqui enviaria ESE formulario y
                           guardaria al ingeniero cuando la persona solo
                           queria fijar un pago. */
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          if ((edicion[r.clave] ?? '') !== aPesos(r.tarifa_centavos)) {
                            void guardar(r);
                          }
                        }}
                        data-prueba={`tarifa-idc-${r.clave}`}
                      />
                    ) : r.tarifa_centavos === null ? (
                      <span className="muted">sin tarifa</span>
                    ) : (
                      aPesos(r.tarifa_centavos)
                    )}
                  </td>
                  {puedeEscribir && (
                    <td>
                      <button
                        type="button"
                        className="btn sm"
                        onClick={() => void guardar(r)}
                        disabled={
                          guardando === r.clave ||
                          (edicion[r.clave] ?? '') === aPesos(r.tarifa_centavos)
                        }
                        data-prueba={`guardar-idc-${r.clave}`}
                      >
                        {guardando === r.clave ? '...' : 'Guardar'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
